import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Mic, Trophy, Palette, Crown, Zap, Check, Star, Users, Building2,
  Radio, GraduationCap, Tv, ArrowRight, ArrowLeft, Loader as Loader2,
  LayoutDashboard, LogOut, Sparkles, Shield, Smartphone, BarChart3,
  Music, Globe, Quote,
} from 'lucide-react';
import { ContestService, ContestTier, CONTEST_INTENT_KEY } from '../services/ContestService';
import { SingAlongService } from '../services/SingAlongService';
import { trackAddToCart, trackInitiateCheckout } from '../lib/analytics';

const tierIcons: Record<string, React.ReactNode> = {
  individual: <Mic size={28} />,
  corporate: <Building2 size={28} />,
  sponsored: <Crown size={28} />,
  subscription: <Zap size={28} />,
};

const useCases = [
  { icon: <Users size={24} />, label: 'House Parties', description: 'Turn any gathering into a singing showdown with friends and family', color: '#38bdf8' },
  { icon: <Building2 size={24} />, label: 'Corporate Events', description: 'Unique team building that gets everyone involved and laughing', color: '#34d399' },
  { icon: <Radio size={24} />, label: 'DJs & Venues', description: 'Weekly karaoke nights with AI scoring and live leaderboards', color: '#f59e0b' },
  { icon: <GraduationCap size={24} />, label: 'Educators', description: 'Music programs and vocal workshops with measurable progress', color: '#a78bfa' },
  { icon: <Tv size={24} />, label: 'Streamers', description: 'Interactive audience singing contests with shareable results', color: '#f472b6' },
  { icon: <Trophy size={24} />, label: 'Tournaments', description: 'Multi-round bracket competitions with automated progression', color: '#fb923c' },
];

const features = [
  { icon: <Star size={22} />, title: 'AI Vocal Scoring', desc: 'Advanced pitch & rhythm analysis scores every performance instantly' },
  { icon: <BarChart3 size={22} />, title: 'Live Leaderboards', desc: 'Real-time rankings keep the competition exciting from start to finish' },
  { icon: <Smartphone size={22} />, title: 'No App Required', desc: 'Participants join and sing from any device with just a link' },
  { icon: <Palette size={22} />, title: 'Custom Branding', desc: 'Add your logo, colors, and sponsor branding to the experience' },
  { icon: <Music size={22} />, title: 'Song Library', desc: 'Curated catalog of backing tracks across genres and decades' },
  { icon: <Shield size={22} />, title: 'Private & Secure', desc: 'Passcode-protected contests keep your event invite-only' },
  { icon: <Globe size={22} />, title: 'Works Anywhere', desc: 'Participants can compete from across the room or across the world' },
  { icon: <Sparkles size={22} />, title: 'Instant Results', desc: 'Detailed performance reports with coaching tips after every song' },
];

const testimonials = [
  { quote: 'We ran a karaoke night at our bar with 40 singers. The leaderboard on the TV had everyone glued. Best Friday night event we have ever hosted.', name: 'Marcus T.', role: 'Bar Owner, Atlanta' },
  { quote: 'Our team building event was a hit. Even the shy folks joined in once they saw how the scoring worked. Already planning the next one.', name: 'Sarah K.', role: 'HR Director' },
  { quote: 'I set up a contest for my daughter\'s birthday party in 10 minutes. The kids were obsessed with trying to beat each other\'s scores.', name: 'James R.', role: 'Parent & Host' },
];

const stats = [
  { value: '10,000+', label: 'Songs Performed' },
  { value: '500+', label: 'Contests Hosted' },
  { value: '98%', label: 'Completion Rate' },
  { value: '4.9/5', label: 'Host Satisfaction' },
];

export const KioskStorefront: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState<ContestTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [accountDest, setAccountDest] = useState('/contests/dashboard');
  const [signingOut, setSigningOut] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const canceled = searchParams.get('canceled') === 'true';

  const startCheckout = async (tier: ContestTier) => {
    setCheckoutError(null);
    setPurchasing(tier.id);
    trackInitiateCheckout(tier.name, tier.price_cents);
    try {
      const url = await ContestService.createCheckout(tier.id);
      window.location.href = url;
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Checkout failed');
      setPurchasing(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await ContestService.getTiers();
        setTiers(data);

        const user = await SingAlongService.getCurrentUser();
        if (user?.email_confirmed_at) {
          setUserEmail(user.email || '');
          try {
            setAccountDest(await SingAlongService.getAccountDestination('contests'));
          } catch {
            setAccountDest('/contests/dashboard');
          }

          const intentSlug = sessionStorage.getItem(CONTEST_INTENT_KEY);
          if (intentSlug) {
            const intended = data.find((t) => t.slug === intentSlug);
            sessionStorage.removeItem(CONTEST_INTENT_KEY);
            if (intended) {
              await startCheckout(intended);
              return;
            }
          }
        }
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load plans');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setActiveTestimonial((i) => (i + 1) % testimonials.length), 6000);
    return () => clearInterval(timer);
  }, []);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await SingAlongService.signOut();
      setUserEmail(null);
    } catch {
      setSigningOut(false);
    }
  };

  const handleSelectTier = async (tier: ContestTier) => {
    setCheckoutError(null);
    trackAddToCart(tier.name, tier.price_cents);
    const user = await SingAlongService.getCurrentUser();
    if (!user || !user.email_confirmed_at) {
      sessionStorage.setItem(CONTEST_INTENT_KEY, tier.slug);
      navigate('/sing-along/signup');
      return;
    }
    await startCheckout(tier);
  };

  return (
    <div className="min-h-screen contest-gradient-bg text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-950/60 backdrop-blur-2xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              aria-label="Go back"
              className="inline-flex items-center gap-2 text-sm font-medium bg-white/5 border border-white/15 text-white/70 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Back</span>
            </button>
            <Link to="/" className="flex items-center gap-3">
              <img src="/TSL_Logo.png" alt="The Singing League" className="h-9" />
              <span className="font-bold text-lg hidden sm:block">The Singing League</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {userEmail ? (
              <>
                <span className="text-sm text-white/50 hidden md:block">{userEmail}</span>
                <button
                  onClick={() => navigate(accountDest)}
                  className="inline-flex items-center gap-2 text-sm font-semibold bg-sky-500 hover:bg-sky-400 text-white px-4 py-2 rounded-lg transition"
                >
                  <LayoutDashboard size={16} />
                  <span className="hidden sm:inline">My Dashboard</span>
                </button>
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="inline-flex items-center gap-2 text-sm font-medium bg-white/5 border border-white/15 text-white/70 hover:text-white hover:bg-white/10 px-4 py-2 rounded-lg transition disabled:opacity-50"
                >
                  <LogOut size={16} />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </>
            ) : (
              <a
                href="#pricing"
                className="text-sm font-semibold bg-sky-500 hover:bg-sky-400 text-white px-4 py-2 rounded-lg transition"
              >
                Get Started
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Animated orbs */}
        <div className="absolute top-[-100px] left-[10%] w-[500px] h-[500px] rounded-full bg-sky-500/8 blur-[120px] contest-orb" />
        <div className="absolute top-[50px] right-[5%] w-[400px] h-[400px] rounded-full bg-emerald-500/6 blur-[100px] contest-orb" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-[-50px] left-[40%] w-[300px] h-[300px] rounded-full bg-sky-400/5 blur-[80px] contest-orb" style={{ animationDelay: '1s' }} />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 text-center">
          <div className="contest-slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 text-sm text-sky-300 mb-8">
              <Sparkles size={14} className="text-sky-400" />
              AI-Powered Singing Competitions
            </div>
          </div>

          <h1 className="contest-slide-up text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[1.05] mb-8" style={{ animationDelay: '0.1s' }}>
            Host Your Own
            <br />
            <span className="relative">
              <span className="bg-gradient-to-r from-sky-400 via-cyan-300 to-emerald-400 bg-clip-text text-transparent">
                Singing Competition
              </span>
            </span>
          </h1>

          <p className="contest-slide-up text-lg sm:text-xl text-white/55 max-w-2xl mx-auto mb-12 leading-relaxed" style={{ animationDelay: '0.2s' }}>
            Real-time vocal scoring, live leaderboards, and fully branded experiences.
            Launch a private contest in minutes — no app download needed.
          </p>

          <div className="contest-slide-up flex flex-col sm:flex-row items-center justify-center gap-4" style={{ animationDelay: '0.3s' }}>
            <a
              href="#pricing"
              className="group flex items-center gap-2 bg-gradient-to-r from-sky-500 to-sky-400 hover:from-sky-400 hover:to-sky-300 text-white font-semibold px-8 py-4 rounded-xl transition-all shadow-lg shadow-sky-500/25 hover:shadow-sky-400/35 hover:-translate-y-0.5"
            >
              View Pricing
              <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
            <Link
              to="/league/karaoke-on-demand-sing-royale"
              className="group flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-white/25 text-white font-medium px-8 py-4 rounded-xl transition-all"
            >
              See Live Example
              <ArrowRight size={16} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </Link>
          </div>

          {/* Floating score badges */}
          <div className="hidden lg:block">
            <div className="absolute top-32 left-[8%] contest-float">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Trophy size={14} className="text-emerald-400" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-white/40">Top Score</div>
                  <div className="text-sm font-bold text-emerald-400">94.7</div>
                </div>
              </div>
            </div>
            <div className="absolute top-48 right-[6%] contest-float-delay-1">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center">
                  <Mic size={14} className="text-sky-400" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-white/40">Singing Now</div>
                  <div className="text-sm font-bold text-sky-400">12 Live</div>
                </div>
              </div>
            </div>
            <div className="absolute bottom-16 left-[14%] contest-float-delay-2">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Star size={14} className="text-amber-400" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-white/40">Rating</div>
                  <div className="text-sm font-bold text-amber-400">4.9/5</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="relative border-y border-white/10 bg-white/[0.02] backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {stats.map((s, i) => (
              <div key={s.label} className="text-center contest-slide-up" style={{ animationDelay: `${0.1 * i}s` }}>
                <div className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-sky-400 to-emerald-400 bg-clip-text text-transparent mb-1">
                  {s.value}
                </div>
                <div className="text-sm text-white/40 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/50 mb-4">
              Simple Setup
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold">Up and Running in Minutes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: '01', title: 'Choose a Plan', desc: 'Select the tier that fits your event size and branding needs.', icon: <Zap size={20} /> },
              { step: '02', title: 'Customize Your Contest', desc: 'Add your brand, choose songs, set rules, and share the invite link.', icon: <Palette size={20} /> },
              { step: '03', title: 'Compete & Crown a Winner', desc: 'Participants sing from any device. AI scores vocals in real time.', icon: <Trophy size={20} /> },
            ].map((item, i) => (
              <div
                key={item.step}
                className="relative group p-8 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-sky-500/30 transition-all duration-300 contest-card-hover"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-sky-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-3xl font-black text-white/10">{item.step}</span>
                    <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400">
                      {item.icon}
                    </div>
                  </div>
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-white/45 text-sm leading-relaxed">{item.desc}</p>
                </div>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 text-white/15">
                    <ArrowRight size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-500/[0.03] via-transparent to-transparent" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/50 mb-4">
              Everything Included
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Packed With Powerful Features</h2>
            <p className="text-white/45 max-w-lg mx-auto">
              Every tool you need to run a professional singing competition, built right in.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f) => (
              <div
                key={f.title}
                className="group p-6 rounded-2xl bg-white/[0.03] border border-white/8 hover:border-sky-500/25 transition-all duration-300 contest-card-hover"
              >
                <div className="w-11 h-11 rounded-xl bg-sky-500/10 border border-sky-500/15 flex items-center justify-center text-sky-400 mb-4 group-hover:bg-sky-500/15 group-hover:border-sky-500/25 transition-colors">
                  {f.icon}
                </div>
                <h3 className="font-semibold text-sm mb-1.5">{f.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/50 mb-4">
              Versatile Platform
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Built For Everyone</h2>
            <p className="text-white/45 max-w-lg mx-auto">
              From backyard parties to professional venues, power singing competitions at any scale.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((uc) => (
              <div
                key={uc.label}
                className="group flex items-start gap-4 p-6 rounded-2xl bg-white/[0.03] border border-white/8 hover:border-white/15 transition-all duration-300 contest-card-hover"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors"
                  style={{ backgroundColor: `${uc.color}15`, color: uc.color, borderWidth: 1, borderColor: `${uc.color}25` }}
                >
                  {uc.icon}
                </div>
                <div>
                  <div className="font-semibold mb-1">{uc.label}</div>
                  <div className="text-white/40 text-sm leading-relaxed">{uc.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-sky-500/[0.02] to-transparent" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/50 mb-4">
              What Hosts Say
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold">Loved by Hosts Everywhere</h2>
          </div>

          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
            >
              {testimonials.map((t, i) => (
                <div key={i} className="w-full flex-shrink-0 px-4">
                  <div className="max-w-2xl mx-auto text-center">
                    <Quote size={32} className="text-sky-500/30 mx-auto mb-6" />
                    <p className="text-lg sm:text-xl text-white/70 leading-relaxed mb-8 italic">
                      "{t.quote}"
                    </p>
                    <div>
                      <div className="font-semibold text-white">{t.name}</div>
                      <div className="text-sm text-white/40">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-2 mt-10">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTestimonial(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === activeTestimonial ? 'w-8 bg-sky-400' : 'w-2 bg-white/20 hover:bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-sky-500/[0.03] to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/50 mb-4">
              Transparent Pricing
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Choose Your Plan</h2>
            <p className="text-white/45 max-w-lg mx-auto">
              No hidden fees. No contracts. Pick a plan and launch your competition today.
            </p>
          </div>

          {canceled && (
            <div className="max-w-xl mx-auto mb-8 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-center text-sm">
              Checkout was canceled. You can pick a plan again whenever you're ready.
            </div>
          )}

          {checkoutError && (
            <div className="max-w-xl mx-auto mb-8 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-center text-sm">
              {checkoutError}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
            </div>
          ) : loadError ? (
            <div className="max-w-xl mx-auto p-6 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-center">
              {loadError}
            </div>
          ) : tiers.length === 0 ? (
            <div className="text-center py-16 text-white/40">
              <p className="text-lg">No plans available at the moment. Check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              {tiers.map((tier) => (
                <div
                  key={tier.id}
                  className={`group relative flex flex-col rounded-2xl border transition-all duration-300 overflow-hidden contest-card-hover ${
                    tier.highlighted
                      ? 'border-emerald-500/40 bg-emerald-500/[0.06]'
                      : 'border-white/10 bg-white/[0.03] hover:border-white/20'
                  }`}
                >
                  {/* Hover glow */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      background: `radial-gradient(300px circle at 50% 0%, ${tier.accent_color}10, transparent 70%)`,
                    }}
                  />

                  {tier.badge && (
                    <div
                      className="absolute top-0 right-0 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-bl-xl"
                      style={{ backgroundColor: tier.accent_color, color: '#0f172a' }}
                    >
                      {tier.badge}
                    </div>
                  )}
                  <div className="relative p-7 flex-1">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110 duration-300"
                      style={{ backgroundColor: `${tier.accent_color}15`, color: tier.accent_color, borderWidth: 1, borderColor: `${tier.accent_color}25` }}
                    >
                      {tierIcons[tier.slug] ?? <Mic size={28} />}
                    </div>
                    <h3 className="font-bold text-xl mb-1.5">{tier.name}</h3>
                    <p className="text-white/45 text-sm mb-6 leading-relaxed">{tier.description}</p>
                    <div className="flex items-baseline gap-1.5 mb-7">
                      <span className="text-4xl font-black">${(tier.price_cents / 100).toFixed(2)}</span>
                      {tier.period_label && <span className="text-white/35 text-sm">{tier.period_label}</span>}
                    </div>
                    <ul className="space-y-3">
                      {tier.features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 shrink-0"
                            style={{ backgroundColor: `${tier.accent_color}15` }}
                          >
                            <Check
                              size={12}
                              strokeWidth={3}
                              style={{ color: tier.accent_color }}
                            />
                          </div>
                          <span className="text-white/65">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="relative p-7 pt-0">
                    <button
                      onClick={() => handleSelectTier(tier)}
                      disabled={!!purchasing}
                      className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50 hover:brightness-110 hover:shadow-lg"
                      style={{
                        backgroundColor: tier.accent_color,
                        color: '#0f172a',
                        boxShadow: `0 8px 24px ${tier.accent_color}20`,
                      }}
                    >
                      {purchasing === tier.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>
                          {tier.billing_mode === 'subscription' ? 'Subscribe' : 'Get Started'}
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-24 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-sky-500/[0.06] rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-5">Ready to Host Your Competition?</h2>
          <p className="text-white/45 mb-10 max-w-lg mx-auto text-lg">
            Join hundreds of hosts running AI-powered singing competitions. Get started in under 5 minutes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#pricing"
              className="group inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-sky-400 hover:from-sky-400 hover:to-sky-300 text-white font-semibold px-8 py-4 rounded-xl transition-all shadow-lg shadow-sky-500/25 hover:shadow-sky-400/35 hover:-translate-y-0.5"
            >
              Choose Your Plan
              <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
            <Link
              to="/league/karaoke-on-demand-sing-royale"
              className="group inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-white/25 text-white font-medium px-8 py-4 rounded-xl transition-all"
            >
              Try a Live Demo
              <ArrowRight size={16} className="opacity-50 group-hover:opacity-100 transition-opacity" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/8 py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-white/30 text-sm">
            <img src="/TSL_Logo.png" alt="" className="h-5 opacity-40" />
            The Singing League
          </div>
          <div className="flex items-center gap-6 text-sm text-white/30">
            <Link to="/privacy" className="hover:text-white/60 transition">Privacy</Link>
            <a href="mailto:info@thesingingleague.com" className="hover:text-white/60 transition">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
